import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import {
  Briefcase,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  AlertCircle
} from 'lucide-react'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Center,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Spinner,
  Textarea
} from '@chakra-ui/react'

interface JobPosting {
  id: string
  title: string
  description: string
  role: 'DRIVER' | 'CONDUCTOR'
  requirements: string[]
  salaryRange?: string
  location?: string
  status: 'DRAFT' | 'ACTIVE' | 'CLOSED'
  postedById: string
  createdAt: string
  updatedAt: string
}

export default function JobsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null)

  // Form states
  const [formData, setFormData] = useState<{
    title: string
    description: string
    role: 'DRIVER' | 'CONDUCTOR'
    requirements: string
    salaryRange: string
    location: string
    status?: 'DRAFT' | 'ACTIVE' | 'CLOSED'
  }>({
    title: '',
    description: '',
    role: 'DRIVER',
    requirements: '',
    salaryRange: '',
    location: '',
    status: 'ACTIVE'
  })

  // Theme values
  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')

  // Queries
  const { data: jobsResponse, isLoading, error } = useQuery(
    ['jobPostings'],
    () => cmsService.getJobPostings()
  )

  // Mutations
  const createMutation = useMutation(
    (data: any) => cmsService.createJobPosting(data),
    {
      onSuccess: () => {
        toast({ title: 'Job created', status: 'success' })
        queryClient.invalidateQueries('jobPostings')
        setIsCreateOpen(false)
        resetForm()
      },
      onError: (err: any) => {
        toast({ title: 'Error creating job', description: err.message, status: 'error' })
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => cmsService.updateJobPosting(id, data),
    {
      onSuccess: () => {
        toast({ title: 'Job updated', status: 'success' })
        queryClient.invalidateQueries('jobPostings')
        setIsEditOpen(false)
        resetForm()
      },
      onError: (err: any) => {
        toast({ title: 'Error updating job', description: err.message, status: 'error' })
      }
    }
  )

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      role: 'DRIVER',
      requirements: '',
      salaryRange: '',
      location: '',
      status: 'ACTIVE'
    })
    setSelectedJob(null)
  }

  const handleOpenCreate = () => {
    resetForm()
    setIsCreateOpen(true)
  }

  const handleOpenEdit = (job: JobPosting) => {
    setSelectedJob(job)
    setFormData({
      title: job.title,
      description: job.description,
      role: job.role,
      requirements: (job.requirements || []).join('\n'),
      salaryRange: job.salaryRange || '',
      location: job.location || '',
      status: job.status
    })
    setIsEditOpen(true)
  }

  const handleCreateSubmit = () => {
    const payload = {
      ...formData,
      requirements: formData.requirements.split('\n').filter(r => r.trim() !== '')
    }
    createMutation.mutate(payload)
  }

  const handleEditSubmit = () => {
    if (!selectedJob) return
    const payload = {
      ...formData,
      requirements: formData.requirements.split('\n').filter(r => r.trim() !== '')
    }
    updateMutation.mutate({ id: selectedJob.id, data: payload })
  }

  const toggleStatus = (job: JobPosting) => {
    const newStatus = job.status === 'ACTIVE' ? 'CLOSED' : 'ACTIVE'
    updateMutation.mutate({
      id: job.id,
      data: { status: newStatus }
    })
  }

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { color: string; label: string }> = {
      DRAFT: { color: 'gray', label: 'Draft' },
      ACTIVE: { color: 'green', label: 'Active' },
      CLOSED: { color: 'red', label: 'Closed' }
    }
    const config = configs[status] || { color: 'gray', label: status }
    return <Badge colorScheme={config.color} borderRadius="full">{config.label}</Badge>
  }

  // Filter jobs
  const jobs: JobPosting[] = jobsResponse?.data || []
  const filteredJobs = jobs.filter(job => {
    const matchSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchRole = roleFilter === 'ALL' || job.role === roleFilter
    const matchStatus = statusFilter === 'ALL' || job.status === statusFilter
    return matchSearch && matchRole && matchStatus
  })

  return (
    <Box p={6} maxW="7xl" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8} wrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="bold">Job Postings</Heading>
          <Text color="gray.500">Manage recruitment and active job listings.</Text>
        </VStack>
        <Button leftIcon={<Plus size={20} />} colorScheme="brand" onClick={handleOpenCreate}>
          Create Job
        </Button>
      </Flex>

      {/* Filters */}
      <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" mb={6}>
        <CardBody p={4}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="space-between" align="center">
            <InputGroup maxW={{ base: 'full', md: '320px' }}>
              <InputLeftElement pointerEvents="none"><Search size={18} color="gray" /></InputLeftElement>
              <Input
                placeholder="Search job title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="lg"
              />
            </InputGroup>

            <HStack spacing={4} wrap="wrap">
              <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} maxW="150px" borderRadius="lg">
                <option value="ALL">All Roles</option>
                <option value="DRIVER">Driver</option>
                <option value="CONDUCTOR">Conductor</option>
              </Select>

              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} maxW="150px" borderRadius="lg">
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="CLOSED">Closed</option>
                <option value="DRAFT">Draft</option>
              </Select>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Main Table */}
      <Card bg={bg} border="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" overflow="hidden">
        {isLoading ? (
          <Center py={16}><Spinner size="xl" color="brand.500" /></Center>
        ) : error ? (
          <Center py={16} flexDir="column">
            <AlertCircle size={40} color="red" />
            <Text mt={4}>Failed to load jobs.</Text>
          </Center>
        ) : filteredJobs.length === 0 ? (
          <Center py={16} flexDir="column">
            <Briefcase size={48} color="gray" />
            <Text mt={4} color="gray.500">No job postings found.</Text>
          </Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead bg={useColorModeValue('gray.50', 'gray.900')}>
                <Tr>
                  <Th>Title</Th>
                  <Th>Role</Th>
                  <Th>Location</Th>
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredJobs.map(job => (
                  <Tr key={job.id} _hover={{ bg: hoverBg }}>
                    <Td fontWeight="medium">{job.title}</Td>
                    <Td><Badge colorScheme={job.role === 'DRIVER' ? 'blue' : 'orange'}>{job.role}</Badge></Td>
                    <Td>{job.location || '-'}</Td>
                    <Td>{new Date(job.createdAt).toLocaleDateString()}</Td>
                    <Td>{getStatusBadge(job.status)}</Td>
                    <Td textAlign="right">
                      <HStack justify="end" spacing={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme={job.status === 'ACTIVE' ? 'red' : 'green'}
                          onClick={() => toggleStatus(job)}
                        >
                          {job.status === 'ACTIVE' ? 'Close' : 'Activate'}
                        </Button>
                        <IconButton
                          aria-label="Edit Job"
                          icon={<Edit2 size={16} />}
                          size="sm"
                          onClick={() => handleOpenEdit(job)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Create / Edit Modal */}
      <Modal isOpen={isCreateOpen || isEditOpen} onClose={() => { setIsCreateOpen(false); setIsEditOpen(false); }} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>{isCreateOpen ? 'Create Job Posting' : 'Edit Job Posting'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Job Title</FormLabel>
                <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Role</FormLabel>
                <Select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value as any })}>
                  <option value="DRIVER">Driver</option>
                  <option value="CONDUCTOR">Conductor</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Location</FormLabel>
                <Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
              </FormControl>

              <FormControl>
                <FormLabel>Salary Range</FormLabel>
                <Input value={formData.salaryRange} onChange={e => setFormData({ ...formData, salaryRange: e.target.value })} placeholder="e.g. 500,000 - 800,000 TZS" />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </FormControl>

              <FormControl>
                <FormLabel>Requirements (One per line)</FormLabel>
                <Textarea value={formData.requirements} onChange={e => setFormData({ ...formData, requirements: e.target.value })} rows={4} placeholder="Valid driving license&#10;5+ years experience" />
              </FormControl>

              {isEditOpen && (
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as any })}>
                    <option value="ACTIVE">Active</option>
                    <option value="CLOSED">Closed</option>
                    <option value="DRAFT">Draft</option>
                  </Select>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); }}>Cancel</Button>
            <Button
              colorScheme="brand"
              onClick={isCreateOpen ? handleCreateSubmit : handleEditSubmit}
              isLoading={createMutation.isLoading || updateMutation.isLoading}
            >
              {isCreateOpen ? 'Create Job' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
